import React, { useState } from "react";
import { Card, Col, InputNumber, Radio, Row, Typography } from "antd";

const { Text } = Typography;

const DEFAULT_HAIRCUT = 15;
const DEFAULT_DAY_BASIS = 364;

type InputMode = "faceValue" | "investment";

interface PricingResult {
  faceValue: number;
  cost: number;
  pricePerHundred: number;
  incomeAtMaturity: number;
  equivalentDiscRate: number;
  maxLoanableAmount: number;
}

function compute(
  faceValue: number,
  rate: number,
  tenor: number,
  dayBasis: number,
  haircut: number,
): PricingResult {
  const r = rate / 100;
  const t = tenor / dayBasis;
  const denominator = 1 + r * t;

  const cost = faceValue / denominator;
  const pricePerHundred = (cost / faceValue) * 100;
  const incomeAtMaturity = faceValue - cost;
  const equivalentDiscRate = (r / denominator) * 100;
  const maxLoanableAmount = cost * (1 - haircut / 100);

  return { faceValue, cost, pricePerHundred, incomeAtMaturity, equivalentDiscRate, maxLoanableAmount };
}

// Investment amount is what the customer pays today (the "cost"); face value is
// what they receive at maturity. compute() only runs forward from face value, so
// when the customer starts from investment amount we invert cost = faceValue / (1 + r*t).
function faceValueFromInvestment(
  investmentAmount: number,
  rate: number,
  tenor: number,
  dayBasis: number,
): number {
  const r = rate / 100;
  const t = tenor / dayBasis;
  return investmentAmount * (1 + r * t);
}

interface TBillPricingCalculatorProps {
  dayBasis?: number;
}

export const TBillPricingCalculator: React.FC<TBillPricingCalculatorProps> = ({
  dayBasis: initialDayBasis = DEFAULT_DAY_BASIS,
}) => {
  const [mode, setMode] = useState<InputMode>("faceValue");
  const [amount, setAmount] = useState<number | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [tenor, setTenor] = useState<number | null>(null);
  const [dayBasis, setDayBasis] = useState<number>(initialDayBasis);
  const [haircut, setHaircut] = useState<number>(DEFAULT_HAIRCUT);

  const resolvedFaceValue =
    amount && amount > 0 && rate && rate > 0 && tenor && tenor > 0
      ? mode === "investment"
        ? faceValueFromInvestment(amount, rate, tenor, dayBasis)
        : amount
      : null;

  const result =
    resolvedFaceValue && rate && rate > 0 && tenor && tenor > 0
      ? compute(resolvedFaceValue, rate, tenor, dayBasis, haircut)
      : null;

  const formatAmount = (value: number) =>
    `GHS ${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const primaryFigures = result
    ? [
        { label: "Face Value", sublabel: "Amount at maturity", value: formatAmount(result.faceValue) },
        { label: "Cost", sublabel: "Total debit to customer", value: formatAmount(result.cost) },
        { label: "Income at Maturity", sublabel: "What the customer earns", value: formatAmount(result.incomeAtMaturity) },
      ]
    : [];

  const secondaryFigures = result
    ? [
        { label: "Price / 100", value: result.pricePerHundred.toFixed(4) },
        { label: "Equivalent Discount Rate", value: `${result.equivalentDiscRate.toFixed(6)}%` },
        { label: "Haircut Applied", value: `${haircut}%` },
        { label: "Maximum Loanable Amount", value: formatAmount(result.maxLoanableAmount) },
      ]
    : [];

  return (
    <div>
      <Card size="small" bordered style={{ borderRadius: 8, marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="faceValue">I know the Face Value</Radio.Button>
            <Radio.Button value="investment">I know the Investment Amount</Radio.Button>
          </Radio.Group>
        </div>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>
                {mode === "investment" ? "Investment Amount (GHS)" : "Face Value (GHS)"}
              </Text>
            </div>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={mode === "investment" ? "e.g. 100,000.00" : "e.g. 113,000.00"}
              value={amount}
              onChange={(v) => setAmount(v)}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v!.replace(/,/g, "") as any}
              min={0}
              precision={2}
            />
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Rate / YTM (%)</Text>
            </div>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="e.g. 13"
              value={rate}
              onChange={(v) => setRate(v)}
              min={0}
              max={100}
              precision={6}
              addonAfter="%"
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Tenor (days)</Text>
            </div>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="e.g. 364"
              value={tenor}
              onChange={(v) => setTenor(v)}
              min={1}
              precision={0}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Day Basis</Text>
            </div>
            <InputNumber
              style={{ width: "100%" }}
              value={dayBasis}
              onChange={(v) => setDayBasis(v ?? DEFAULT_DAY_BASIS)}
              min={360}
              max={366}
              precision={0}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Haircut (%)</Text>
            </div>
            <InputNumber
              style={{ width: "100%" }}
              value={haircut}
              onChange={(v) => setHaircut(v ?? DEFAULT_HAIRCUT)}
              min={0}
              max={100}
              precision={2}
              addonAfter="%"
            />
          </Col>
        </Row>
      </Card>

      {result && (
        <Card size="small" bordered title="Results" style={{ borderRadius: 8 }}>
          <Row gutter={12}>
            {primaryFigures.map((figure) => (
              <Col span={8} key={figure.label}>
                <div
                  style={{
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    padding: "12px 14px",
                    height: "100%",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>{figure.label}</div>
                  <div style={{ fontSize: 11, color: "#bfbfbf", marginBottom: 8 }}>
                    {figure.sublabel}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#1f1f1f" }}>
                    {figure.value}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <div style={{ marginTop: 20 }}>
            {secondaryFigures.map((figure, index) => (
              <div
                key={figure.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 2px",
                  borderBottom:
                    index === secondaryFigures.length - 1 ? "none" : "1px solid #f0f0f0",
                }}
              >
                <Text type="secondary">{figure.label}</Text>
                <Text strong>{figure.value}</Text>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!result && (
        <div
          style={{
            padding: "20px",
            background: "#fafafa",
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
            textAlign: "center",
            color: "#8c8c8c",
          }}
        >
          Enter {mode === "investment" ? "investment amount" : "face value"},{" "}
          rate, and tenor above to see results
        </div>
      )}
    </div>
  );
};
